import React from 'react';
import { ArrowLeft } from 'lucide-react';

interface BackButtonProps {
  /**
   * The text label to display next to the arrow icon
   * @default "Tilbake"
   */
  label?: string;

  /**
   * Additional CSS classes to apply to the button
   * Will be merged with the default classes
   */
  className?: string;

  /**
   * Click handler for the button
   * If not provided, will use window.history.back()
   */
  onClick?: () => void | Promise<void>;

  /**
   * Optional callback to execute before navigation
   * Can be async for cleanup or validation operations
   */
  onBeforeNavigate?: () => void | Promise<void>;

  /**
   * Size of the arrow icon
   * @default 20
   */
  iconSize?: number;

  /**
   * Hide the visible label below the `sm` breakpoint (handheld terminals).
   * The button keeps its aria-label, so it stays accessible.
   * @default false
   */
  hideLabelOnMobile?: boolean;
}

/**
 * Reusable back button component
 *
 * Features:
 * - Supports custom onClick handler or defaults to window.history.back()
 * - Optional callback before navigation for cleanup/validation
 * - Customizable label and styling
 * - Accessible button with proper hover states
 *
 * @example
 * ```tsx
 * // Basic usage with custom handler
 * <BackButton onClick={() => setView('list')} />
 *
 * // Custom label
 * <BackButton onClick={handleBack} label="Gå tilbake" />
 *
 * // With custom styling
 * <BackButton onClick={handleBack} className="ml-4" />
 *
 * // With before-navigate callback
 * <BackButton onClick={handleBack} onBeforeNavigate={async () => await saveChanges()} />
 *
 * // Use browser history (default if no onClick provided)
 * <BackButton />
 * ```
 */
export const BackButton: React.FC<BackButtonProps> = ({
  label = 'Tilbake',
  className = '',
  onClick,
  onBeforeNavigate,
  iconSize = 20,
  hideLabelOnMobile = false,
}) => {
  const handleClick = async () => {
    // Execute optional callback before navigation
    if (onBeforeNavigate) {
      await onBeforeNavigate();
    }

    // Call custom onClick handler or fallback to browser history
    if (onClick) {
      await onClick();
    } else {
      window.history.back();
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      className={`flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors ${className}`}
      aria-label={label}
    >
      <ArrowLeft size={iconSize} />
      <span className={hideLabelOnMobile ? 'hidden sm:inline' : undefined}>{label}</span>
    </button>
  );
};

export default BackButton;
